import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { preRegistrationsApi } from "../api/preRegistrations";
import { qk } from "../../../../lib/queryKeys";
import { useClientScope } from "../../../../shared/contexts";

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

export const PreRegistrations: React.FC = () => {
  const queryClient = useQueryClient();
  const { isCentralAdmin, effectiveClientId } = useClientScope();
  const [answersOpen, setAnswersOpen] = React.useState(false);
  const [selectedEmail, setSelectedEmail] = React.useState<string | null>(null);
  const [selectedEmails, setSelectedEmails] = React.useState<string[]>([]);
  const [bulkSummary, setBulkSummary] = React.useState<{
    approved: number;
    failed: string[];
  } | null>(null);

  const listQuery = useQuery({
    queryKey: qk.preRegistrations(effectiveClientId ?? null),
    queryFn: () => preRegistrationsApi.list(effectiveClientId),
  });

  const entries = listQuery.data?.data ?? [];

  React.useEffect(() => {
    setSelectedEmails((current) =>
      current.filter((email) => entries.some((entry) => entry.email === email))
    );
  }, [entries]);

  const answersQuery = useQuery({
    queryKey: ["pre-registration-answers", selectedEmail],
    queryFn: () =>
      selectedEmail ? preRegistrationsApi.getAnswers(selectedEmail) : null,
    enabled: Boolean(selectedEmail),
  });

  const approveMutation = useMutation({
    mutationFn: async (emails: string[]) => {
      const failed: string[] = [];
      let approved = 0;

      for (const email of emails) {
        try {
          await preRegistrationsApi.approve(email);
          approved += 1;
        } catch (_error) {
          failed.push(email);
        }
      }

      return { approved, failed };
    },
    onSuccess: (result) => {
      setBulkSummary(result);
      queryClient.invalidateQueries({
        queryKey: qk.preRegistrations(effectiveClientId ?? null),
      });
      setSelectedEmails([]);
    },
  });

  const allSelected = entries.length > 0 && selectedEmails.length === entries.length;
  const hasSelection = selectedEmails.length > 0;

  return (
    <Box>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Pre-Registrations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Approve pending registrations to create accounts and send onboarding emails.
          </Typography>
          {!isCentralAdmin && !effectiveClientId && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No client assigned to your account.
            </Alert>
          )}
          {bulkSummary && (
            <Alert
              severity={bulkSummary.failed.length ? "warning" : "success"}
              sx={{ mt: 2 }}
            >
              Approved {bulkSummary.approved} registration
              {bulkSummary.approved === 1 ? "" : "s"}.
              {bulkSummary.failed.length
                ? ` Failed: ${bulkSummary.failed.join(", ")}`
                : ""}
            </Alert>
          )}
        </Box>

        {listQuery.isLoading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading pre-registrations...</Typography>
          </Box>
        ) : listQuery.isError ? (
          <Alert severity="error">Failed to load pre-registrations.</Alert>
        ) : entries.length ? (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
              <Button
                variant="contained"
                disabled={!hasSelection || approveMutation.isPending}
                onClick={() => {
                  setBulkSummary(null);
                  approveMutation.mutate(selectedEmails);
                }}
              >
                {approveMutation.isPending
                  ? "Approving..."
                  : `Approve Selected (${selectedEmails.length})`}
              </Button>
              <Typography variant="body2" color="text.secondary">
                Select one or more registrations to bulk approve.
              </Typography>
            </Stack>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={hasSelection && !allSelected}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedEmails(entries.map((entry) => entry.email));
                        } else {
                          setSelectedEmails([]);
                        }
                      }}
                      inputProps={{ "aria-label": "select all pre-registrations" }}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => {
                  const checked = selectedEmails.includes(entry.email);
                  return (
                    <TableRow key={entry.email} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={checked}
                          onChange={() =>
                            setSelectedEmails((current) =>
                              current.includes(entry.email)
                                ? current.filter((email) => email !== entry.email)
                                : [...current, entry.email]
                            )
                          }
                          inputProps={{ "aria-label": `select ${entry.email}` }}
                        />
                      </TableCell>
                      <TableCell>{entry.name || "-"}</TableCell>
                      <TableCell>{entry.email}</TableCell>
                      <TableCell>{entry.client_name || entry.client_id || "-"}</TableCell>
                      <TableCell>{formatDateTime(entry.submitted_at)}</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setSelectedEmail(entry.email);
                            setAnswersOpen(true);
                          }}
                        >
                          View answers
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        ) : (
          <Alert severity="info">No pending pre-registrations.</Alert>
        )}
      </Stack>

      <Dialog
        open={answersOpen}
        onClose={() => {
          setAnswersOpen(false);
          setSelectedEmail(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Pre-registration answers</DialogTitle>
        <DialogContent>
          {answersQuery.isLoading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading answers...</Typography>
            </Box>
          ) : answersQuery.isError ? (
            <Alert severity="error">Failed to load answers.</Alert>
          ) : answersQuery.data?.data ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Email: {answersQuery.data.data.email}
              </Typography>
              {answersQuery.data.data.client_name && (
                <Typography variant="body2" color="text.secondary">
                  Client: {answersQuery.data.data.client_name}
                </Typography>
              )}
              <Stack spacing={1.5}>
                {answersQuery.data.data.answers.map((answer) => {
                  const value = Array.isArray(answer.answer)
                    ? answer.answer.join(", ")
                    : answer.answer ?? "-";
                  return (
                    <Box key={answer.question_id}>
                      <Typography variant="subtitle2">
                        {answer.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {String(value)}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No answers found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
